import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import PropTypes from 'prop-types';
import { Form, Row, Col } from 'react-bootstrap';
import { BaseModal, Button, Icon, LoadingSpinner } from 'yankees-react-utils';
import { useOktaAuth } from '@okta/okta-react';
import { UserReport } from '../data';
import usePlayerSelector from '../utils/usePlayerSelector';

const FeedbackModal = ({ showModal, setShowModal, modalContainer }) => {
    const { t: translate } = useTranslation();
    const { authState: { accessToken } } = useOktaAuth();
    const { PlayerID, FullName, Team } = usePlayerSelector();

    const [type, setType] = useState('');
    const [summary, setSummary] = useState('');
    const [details, setDetails] = useState('');
    const [fileId, setFileId] = useState(1);
    const [files, setFiles] = useState([{ fileId: 0 }]);
    const [submitting, setSubmitting] = useState(false);
    const [submitted, setSubmitted] = useState(false);
    const [submittingError, setSubmittingError] = useState(false);

    const resetForm = () => {
        setType('');
        setSummary('');
        setDetails('');
        setFiles([{ fileId: 0 }]);
        setSubmitted(false);
        setSubmitting(false);
    };

    const onDoSubmit = async () => {
        setSubmitting(true);
        const report = {
            Type: type,
            Url: window.location.href,
            UserAgent: window.navigator.userAgent,
            Subject: summary,
            UserComments: details,
            LoggedInPlayerId: PlayerID,
            LoggedInPlayerName: FullName,
            LoggedInPlayerTeam: Team?.FullName,
            LanguageDisplayed: translate('languageNameEnglish'),
        };

        const fd = new FormData();
        fd.append('UserReport', JSON.stringify(report));
        files.filter((f) => f.file).forEach((f, i) => {
            fd.append(`file${i}`, f.file);
        });

        await UserReport.post(accessToken, fd)
            .then((response) => {
                console.log(`Feedback submitted: ${JSON.stringify(response)}`);
                resetForm();
                setSubmitted(true);
            })
            .catch((exception) => {
                exception.json().then((e) => console.log(`POST failed: ${e?.Message}`));
                setSubmittingError(true);
            })
            .finally(() => {
                setSubmitting(false);
            });
    };

    const onDoClose = () => {
        resetForm();
        setShowModal(false);
    };

    const setFile = (_fileId, file) => {
        const copy = [...files];
        const copyFile = copy.find((f) => f.fileId === _fileId);
        copyFile.file = file;
        setFiles(copy);
    };
    const addFile = () => {
        const copy = [...files];
        copy.push({
            fileId,
        });
        setFileId(fileId + 1);
        setFiles(copy);
    };
    const removeFile = (_fileId) => {
        const copy = [...files].filter((f) => f.fileId !== _fileId);
        setFiles(copy);
    };
    return (
        <BaseModal
            show={showModal}
            onHide={() => onDoClose()}
            container={modalContainer}
        >
            <BaseModal.Header>
                {translate('nav.submit-feedback')}
            </BaseModal.Header>
            <BaseModal.Body>
                {submittingError && (
                    <Row>
                        <Col md={2}>
                            <Icon icon="cross" color="red" size="2em" />
                        </Col>
                        <Col md={10}>
                            <br />
                            {translate('nav.render-error')}
                            <br /><br />
                        </Col>
                        <hr />
                    </Row>
                )}
                {!submitted && !submitting && (
                    <>
                        <Form.Group>
                            <Form.Label className="mb-1">{translate('feedback.type')}</Form.Label>
                            <Form.Control
                                as="select"
                                value={type}
                                onChange={(e) => setType(e.target.value)}
                            >
                                <option disabled value="">{translate('feedback.select-type')}</option>
                                <option value="1">{translate('feedback.report-bug')}</option>
                                <option value="2">{translate('feedback.request-feature')}</option>
                                <option value="10">{translate('feedback.translation-problem')}</option>
                            </Form.Control>
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="mb-1">{translate('feedback.summary')}</Form.Label>
                            <Form.Control
                                type="text"
                                placeholder={translate('feedback.enter-summary')}
                                value={summary}
                                onChange={(e) => setSummary(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Group>
                            <Form.Label className="mb-1">{translate('feedback.details')}</Form.Label>
                            <Form.Control
                                type="text"
                                as="textarea"
                                placeholder={translate('feedback.enter-details')}
                                rows={3}
                                value={details}
                                onChange={(e) => setDetails(e.target.value)}
                            />
                        </Form.Group>
                        <Form.Label className="mb-1">{translate('feedback.attachments')}</Form.Label>
                    </>
                )}
                {!submitted && !submitting && files.map((file, index) => (
                    <div key={`file-${file.fileId}`} className="d-flex align-items-center">
                        <Form.Control type="file" onChange={(e) => setFile(file.fileId, e.target.files[0])} />
                        {!!index && <Icon icon="cross" size="1em" onClick={() => removeFile(file.fileId)} className="mb-1 ms-1" /> }
                    </div>
                ))}
                {!submitted && !submitting && (
                    <>
                        <div className="d-flex justify-content-center mt-1">
                            <Button onClick={addFile}>{translate('feedback.add-attachment')}</Button>
                        </div>
                    </>
                )}
                {submitting && <><LoadingSpinner /><br /><br />{translate('common.submitting')}</>}
                {submitted && <><Icon icon="checkmark" color="green" size="2.5em" style={{ marginRight: '10px' }} /><b>{translate('feedback.report-submitted')}</b></>}
            </BaseModal.Body>
            <BaseModal.Footer>
                {!submitted && !submitting && (
                    <>
                        <Button variant="secondary" onClick={() => setShowModal(false)}>{translate('common.cancel')}</Button>
                        <Button variant="primary" onClick={onDoSubmit} disabled={!type || !summary || !details}>{translate('common.submit')}</Button>
                    </>
                )}
                {submitted && (
                    <Button variant="primary" onClick={onDoClose}>{translate('common.close')}</Button>
                )}
            </BaseModal.Footer>
        </BaseModal>
    );
};

FeedbackModal.propTypes = {
    showModal: PropTypes.bool.isRequired,
    setShowModal: PropTypes.func.isRequired,
    modalContainer: PropTypes.any.isRequired, // eslint-disable-line react/forbid-prop-types
};

export default FeedbackModal;
